// What a form shows, and what it sends.

import { COLLECTIONS, REFERENCES, type Field } from '../schema';
import { today } from '../format';
import type { Row } from '../types';

/** Fields the form no longer shows, though the column and what it holds stay.
 *
 *  Each was taken off the New Barrier form at the users' request, in their
 *  review of it. Leaving `editableFields` takes a field out of everything a form
 *  does with it: it is not rendered, its list is not fetched, and it is not in
 *  the request body -- so an edit leaves the stored value as it was rather than
 *  writing back what was loaded, and a new row leaves it null, or sends its
 *  default where it has one (`createBody`). The grids still show it, and still
 *  edit it inline where they did. */
const NOT_ON_FORM: Record<string, readonly string[]> = {
  barriers: [
    'buyer_status_id',
    // Action Type `MAKE` with its To Plant says what these said, so both halves
    // go together. A plant left on the form without its checkbox could not be
    // saved: `CK_barrier_make_parts_site` refuses a site unless `make_parts` is 1.
    'make_parts',
    'make_parts_site_id',
    // Both commitment dates, which the users marked "OK to remove". The grids
    // still show both and edit them inline, and the dashboard still shows the
    // current one.
    'original_commitment_date',
    'current_commitment_date',
    // Neither is on the layout the users drew for the form. Date Of Escalation
    // is still required, so a new barrier is sent today's, which the grids edit
    // from there.
    'date_of_escalation',
    'root_cause_id',
  ],
};

/** The order a form lays its fields out in, where the schema's is not the one
 *  its users work in. It is the order a keyboard moves through them, and the
 *  order they stack in where the screen is too narrow for `FORM_LAYOUT`.
 *
 *  The barrier form follows the layout the users drew for it, read across each
 *  row, and down the block beneath the first row before carrying on across.
 *
 *  A field not listed follows the listed ones, in schema order, so a column
 *  added later still reaches the form until somebody places it. */
const FORM_ORDER: Record<string, readonly string[]> = {
  barriers: [
    'order_id',
    'product_id',
    'site_id',
    'is_hot',
    'problem_description',
    'corrective_action',
    'area_id',
    'part_number',
    'week_1_need_qty',
    'week_2_need_qty',
    'commodity_id',
    'cycle_count_needed',
    // The source's Action Category, decomposed: the type here, and the plants
    // it names on the row below.
    'action_type',
    'action_owner',
    'department_id',
    'warehouse_status_id',
    'origin_site_id',
    'dest_site_id',
    // The source's one `TO# / PO#` field, now two.
    'interplant_transfer_ref',
    'po_number',
    'actual_close_date',
  ],
};

export interface FormLayout {
  /** As `grid-template-columns` takes them. */
  readonly columns: string;
  /** As `grid-template-areas` takes them: each row names the field in each of
   *  its cells, `.` for an empty one, and a field named in several cells spans
   *  them. */
  readonly rows: readonly string[];
}

/** Where a form places each field, where a list in `FORM_ORDER` is not enough.
 *
 *  The barrier form is the layout the users drew for it on 2026-10-16: the two
 *  long texts side by side and tall, the quantities stacked beside them, and
 *  the two checkboxes in a narrow column of their own. A field the layout does
 *  not name still renders, in the first empty cell. */
const FORM_LAYOUT: Record<string, FormLayout> = {
  barriers: {
    columns: 'repeat(3, minmax(0, 1fr)) auto',
    rows: [
      'order_id                product_id         site_id            is_hot',
      'problem_description     corrective_action  area_id            cycle_count_needed',
      'problem_description     corrective_action  part_number        .',
      'problem_description     corrective_action  week_1_need_qty    .',
      'problem_description     corrective_action  week_2_need_qty    .',
      'problem_description     corrective_action  commodity_id       .',
      'action_type             action_owner       department_id      .',
      'warehouse_status_id     origin_site_id     dest_site_id       .',
      'interplant_transfer_ref po_number          actual_close_date  .',
    ],
  },
};

export function formLayout(slug: string): FormLayout | null {
  return FORM_LAYOUT[slug] ?? null;
}

/** The fields a layout places, each once. */
export function laidOutFields(layout: FormLayout): Set<string> {
  return new Set(layout.rows.flatMap((row) => row.trim().split(/\s+/)).filter((name) => name !== '.'));
}

/** Fields a form shows as a person chosen from the directory: the name column
 *  the picker shows, mapped to the column holding that person's object id.
 *
 *  The pair is one field to a person and two columns to the API. The form
 *  shows the name and sends the id, and only the id: the API looks the person
 *  up and writes the name the directory gives, and it refuses a name typed by
 *  hand. So the id column is not a field of its own (`editableFields` leaves it
 *  out) and the name column is never in a request body (`toBody`). */
export const PERSON_FIELDS: Record<string, Readonly<Record<string, string>>> = {
  barriers: { action_owner: 'action_owner_object_id' },
};

/** The object id column behind a person field, or undefined for any other. */
export function personIdColumn(slug: string, name: string): string | undefined {
  return PERSON_FIELDS[slug]?.[name];
}

/** The fields a person may fill in: writable, not the key, and not one the
 *  form has dropped, in the order the form lays them out.
 *
 *  The four audit columns are not among them and cannot be. The API writes them
 *  from the token -- `created_by` from the signed-in user's sign-in name rather
 *  than the default `SUSER_SNAME()`, which would record every row in the system
 *  as created by the web app's managed identity.
 *
 *  Nor is the object id behind a person field: the person's name stands for
 *  the pair, and `PERSON_FIELDS` says which column holds the id. */
export function editableFields(slug: string): Field[] {
  const collection = COLLECTIONS[slug];
  if (!collection) throw new Error(`no such collection: ${slug}`);
  const dropped = [...(NOT_ON_FORM[slug] ?? []), ...Object.values(PERSON_FIELDS[slug] ?? {})];
  const fields = collection.fields.filter(
    (field) => field.writable && field.name !== collection.pk && !dropped.includes(field.name)
  );
  const order = FORM_ORDER[slug];
  if (!order) return fields;
  const rank = (name: string) => (order.includes(name) ? order.indexOf(name) : order.length);
  // A stable sort, so unlisted fields keep the schema's order among themselves.
  return [...fields].sort((a, b) => rank(a.name) - rank(b.name));
}

/** The foreign keys on a collection, so a form knows which lists to fetch. */
export function referenceColumns(slug: string): string[] {
  return editableFields(slug)
    .map((field) => field.name)
    .filter((name) => Boolean(REFERENCES[name]));
}

/** Lists a form offers only part of, as the API filter that selects the part.
 *
 *  A closed job stays on every barrier that already names it -- that is the
 *  record -- but is no longer offered, so a barrier cannot be raised against
 *  one. Only `Open` is offered rather than everything not `Closed`: `status` is
 *  null on the four source orders with no number, and the schema keeps those
 *  out of every open-orders count for the same reason. */
export const OFFERED: Record<string, Record<string, Record<string, string>>> = {
  barriers: { order_id: { status: 'Open' } },
};

/** A list's options, sorted by what a person reads, and cut to the offered
 *  part where there is one.
 *
 *  `keep` is the value the form opened with, and it stays in the list even when
 *  it is no longer offered. Editing a barrier on a closed job has to show that
 *  job as its value; a select whose value is not among its options renders as
 *  `(none)`, and the save would then look as if it had cleared the field. */
export function offeredOptions(
  lookup: ReadonlyMap<number, string> | undefined,
  offered: ReadonlySet<number> | null,
  keep: unknown
): [number, string][] {
  return [...(lookup?.entries() ?? [])]
    .filter(([key]) => offered === null || offered.has(key) || key === keep)
    .sort((a, b) => a[1].localeCompare(b[1]));
}

/** The source's own form defaults, which `#59` lists: `Date Of Escalation`,
 *  `Daily Production.Date` and both commitment dates default to today. The
 *  commitment dates have left the form, so a new barrier has no commitment
 *  until somebody sets one rather than one nobody chose. Date Of Escalation has
 *  left it too, but the column is required, so its default is still sent. Keys
 *  auto-generate, which here means the database's identity column does it and
 *  nothing is sent. */
const TODAY_DEFAULTS: Record<string, string[]> = {
  barriers: ['date_of_escalation'],
  'daily-production': ['production_date'],
};

export function newRow(slug: string): Row {
  const row: Row = {};
  for (const field of editableFields(slug)) {
    row[field.name] = field.type === 'bool' ? false : null;
  }
  for (const idColumn of Object.values(PERSON_FIELDS[slug] ?? {})) row[idColumn] = null;
  for (const name of TODAY_DEFAULTS[slug] ?? []) row[name] = today();
  return row;
}

/** A new row pre-populated from an existing one -- the source's single custom
 *  action, `Duplicate Barrier`.
 *
 *  Two departures from a plain copy, and both are judgements this phase made
 *  rather than behaviour the source documented: the duplicate opens today
 *  rather than on the original's escalation date, and it opens *open* -- a
 *  duplicate carrying the original's close date would be a closed barrier
 *  nobody closed.
 *
 *  A person field copies the person. A name typed before Action Owner came
 *  from the directory names nobody, and the API will not take it as a new
 *  value, so the duplicate starts with no Action Owner rather than one that
 *  cannot be saved.
 *
 *  Hot is copied for a person an Admin has flagged to change it, and nobody
 *  else: the API refuses a new barrier marked Hot from anyone without the flag,
 *  so for them the duplicate starts not Hot rather than as one that cannot be
 *  saved. */
export function duplicateOf(slug: string, source: Row, canSetHot = false): Row {
  const row = newRow(slug);
  for (const field of editableFields(slug)) {
    if (field.name in source) row[field.name] = source[field.name];
  }
  for (const [name, idColumn] of Object.entries(PERSON_FIELDS[slug] ?? {})) {
    const person = source[idColumn] ?? null;
    row[idColumn] = person;
    if (person === null) row[name] = null;
  }
  for (const name of TODAY_DEFAULTS[slug] ?? []) row[name] = today();
  if (slug === 'barriers') {
    row.actual_close_date = null;
    if (!canSetHot) row.is_hot = false;
  }
  return row;
}

/** What goes in the request body: every editable field, with the empty ones as
 *  null rather than as empty strings. An edit sends this; `createBody` adds to
 *  it for a new row.
 *
 *  A person field sends the person's object id, and only when the person
 *  changed from `loaded`, the row the form opened with -- or, for a new row,
 *  when there is one. The API looks a person up in the directory each time it
 *  is sent one, so sending an unchanged person would make every save depend on
 *  the directory answering. Clearing a name typed before the change sends a
 *  null id, which is what clears it. */
export function toBody(slug: string, row: Row, loaded: Row | null = null): Row {
  const body: Row = {};
  for (const field of editableFields(slug)) {
    const idColumn = personIdColumn(slug, field.name);
    if (idColumn) {
      const person = row[idColumn] ?? null;
      const changed =
        loaded === null
          ? person !== null
          : person !== (loaded[idColumn] ?? null) || (row[field.name] ?? null) !== (loaded[field.name] ?? null);
      if (changed) body[idColumn] = person;
      continue;
    }
    const value = row[field.name];
    if (field.type === 'bool') {
      body[field.name] = Boolean(value);
      continue;
    }
    if (value === '' || value === undefined) {
      body[field.name] = null;
      continue;
    }
    body[field.name] = field.type === 'int' && value !== null ? Number(value) : value;
  }
  return body;
}

/** A new row's request body: `toBody`, and the today defaults of any field the
 *  form does not show. Date Of Escalation is required and has left the form, so
 *  a barrier is escalated the day it is raised, or duplicated. An edit sends
 *  none of these, and leaves what is stored. */
export function createBody(slug: string, row: Row): Row {
  const body = toBody(slug, row);
  for (const name of TODAY_DEFAULTS[slug] ?? []) {
    if (!(name in body)) body[name] = row[name] ?? today();
  }
  return body;
}
